import { Link, useLocation } from 'react-router-dom';
import { IconEmpty } from 'shared/components/Icons';
import { crumbsFromPath } from 'layout/navigation';
import { useI18n } from 'core/i18n/I18nProvider';

export function Breadcrumbs({ items }) {
  const { t } = useI18n();
  const location = useLocation();
  const crumbs = items || crumbsFromPath(location.pathname, t);
  if (!crumbs.length) {
    return null;
  }
  return (
    <nav className="breadcrumbs" aria-label={t('layout.breadcrumb')}>
      {crumbs.map((crumb, index) => {
        const last = index === crumbs.length - 1;
        return (
          <span key={crumb.to} className="breadcrumb-item">
            {last ? (
              <span aria-current="page">{crumb.label}</span>
            ) : (
              <Link to={crumb.to}>{crumb.label}</Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  secondary,
  overflow,
  backTo,
  status,
  crumbs,
}) {
  const { t } = useI18n();
  return (
    <header className="page-header">
      <div className="page-header-copy">
        <Breadcrumbs items={crumbs} />
        {backTo ? (
          <Link className="page-back" to={backTo}>{t('actions.back')}</Link>
        ) : null}
        <div className="page-title-row">
          <h1>{title}</h1>
          {status}
        </div>
        {description ? <p className="muted">{description}</p> : null}
      </div>
      {actions || secondary || overflow ? (
        <div className="page-header-actions">
          {secondary}
          {actions}
          {overflow}
        </div>
      ) : null}
    </header>
  );
}

export function EmptyState({ title, description, action, icon = true }) {
  return (
    <div className="empty-state">
      {icon ? <IconEmpty /> : null}
      <p className="empty-title">{title}</p>
      {description ? <p className="muted">{description}</p> : null}
      {action}
    </div>
  );
}

export function ErrorState({ title, onRetry, retryLabel }) {
  return (
    <div className="error-state" role="alert">
      <p>{title}</p>
      {onRetry ? (
        <button type="button" className="btn btn-secondary" onClick={onRetry}>
          {retryLabel}
        </button>
      ) : null}
    </div>
  );
}

export function Skeleton({ rows = 4 }) {
  return (
    <div className="skeleton-stack" aria-hidden="true">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="skeleton-row" />
      ))}
    </div>
  );
}

export function TableSkeleton({ columns = 5, rows = 6 }) {
  return (
    <div className="table-wrap" aria-hidden="true">
      <table className="data-table">
        <thead>
          <tr>
            {Array.from({ length: columns }).map((_, index) => (
              <th key={index}><span className="skeleton-row" /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columns }).map((__, colIndex) => (
                <td key={colIndex}><span className="skeleton-row" /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CardSkeleton({ count = 4 }) {
  return (
    <div className="stat-grid" aria-hidden="true">
      {Array.from({ length: count }).map((_, index) => (
        <section key={index} className="card">
          <div className="skeleton-row" />
          <div className="skeleton-row" />
        </section>
      ))}
    </div>
  );
}

export function Badge({ children, tone = 'neutral', color }) {
  return (
    <span className={`badge badge-${tone} status-chip`} style={color ? { '--badge-color': color } : undefined}>
      {children}
    </span>
  );
}

export function Card({ children, className = '', ...props }) {
  return <section className={`card ${className}`.trim()} {...props}>{children}</section>;
}

export function ConflictAlert({ message, onRefresh, refreshLabel }) {
  if (!message) {
    return null;
  }
  return (
    <div className="warning-banner conflict-banner" role="alert">
      <p>{message}</p>
      {onRefresh ? (
        <button type="button" className="btn btn-secondary" onClick={onRefresh}>{refreshLabel}</button>
      ) : null}
    </div>
  );
}
